import type { Proyeccion } from '../api'
import { num } from '../formato'

/**
 * Una proyeccion del motor tal cual: el grafico (historial + proyeccion + intervalo), el
 * error medido fuera de muestra y la explicacion del metodo. Aqui no se calcula nada.
 */

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
const pctTexto = (v: number | null) => (v === null ? '—' : v.toLocaleString('es-EC'))

export const nombreMes = (periodo: string) => `${MESES[Number(periodo.slice(5)) - 1]} ${periodo.slice(0, 4)}`

const ANCHO = 720
const ALTO = 190
const M = { arriba: 12, derecha: 12, abajo: 24, izquierda: 56 }

const METODO: Record<Proyeccion['metodo'], string> = {
  estacional: 'Modelo estacional (Ridge)',
  promedio: 'Promedio simple',
  insuficiente: 'Sin proyección',
}

function Grafico({ historial, proyeccion, unidad }: { historial: { periodo: string; valor: number }[]; proyeccion: Proyeccion; unidad: string }) {
  const todos = [...historial.map((h) => h.periodo), ...proyeccion.meses.map((m) => m.periodo)]
  const indice = (p: string) => Number(p.slice(0, 4)) * 12 + Number(p.slice(5)) - 1
  const x0 = indice(todos[0]!)
  const x1 = indice(todos[todos.length - 1]!)
  const max = Math.max(...historial.map((h) => h.valor), ...proyeccion.meses.map((m) => m.superior)) * 1.05 || 1
  const x = (p: string) => M.izquierda + ((indice(p) - x0) / Math.max(1, x1 - x0)) * (ANCHO - M.izquierda - M.derecha)
  const y = (v: number) => M.arriba + (1 - v / max) * (ALTO - M.arriba - M.abajo)
  const linea = (puntos: { periodo: string; valor: number }[]) => puntos.map((p, i) => `${i ? 'L' : 'M'}${x(p.periodo)},${y(p.valor)}`).join(' ')
  const banda =
    proyeccion.meses.map((m, i) => `${i ? 'L' : 'M'}${x(m.periodo)},${y(m.superior)}`).join(' ') +
    [...proyeccion.meses].reverse().map((m) => ` L${x(m.periodo)},${y(m.inferior)}`).join('') +
    ' Z'
  const marcas = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max)

  return (
    <figure className="grafico">
      <svg viewBox={`0 0 ${ANCHO} ${ALTO}`} role="img" aria-label={`Historial y proyección en ${unidad}`}>
        {marcas.map((v) => (
          <g key={v}>
            <line className="rejilla" x1={M.izquierda} x2={ANCHO - M.derecha} y1={y(v)} y2={y(v)} />
            <text className="eje" x={M.izquierda - 6} y={y(v) + 4} textAnchor="end">
              {num(Math.round(v))}
            </text>
          </g>
        ))}
        <path d={banda} className="banda-proyeccion" />
        {historial.length > 0 && <path d={linea(historial)} className="linea-historial" />}
        <path d={linea(proyeccion.meses)} className="linea-proyeccion" />
        {[todos[0]!, proyeccion.meses[0]!.periodo, todos[todos.length - 1]!].map((p, i) => (
          <text key={p} className="eje" x={x(p)} y={ALTO - 6} textAnchor={(['start', 'middle', 'end'] as const)[i]}>
            {nombreMes(p)}
          </text>
        ))}
      </svg>
      <figcaption className="suave">
        <span className="leyenda leyenda-historial" /> Historial <span className="leyenda leyenda-proyeccion" /> Proyección{' '}
        <span className="leyenda leyenda-banda" /> Intervalo del 80 %
      </figcaption>
    </figure>
  )
}

interface Props {
  proyeccion: Proyeccion
  historial?: { periodo: string; valor: number }[]
  unidad: 'kWh' | 'kW'
}

export function ProyeccionConsumo({ proyeccion: p, historial = [], unidad }: Props) {
  if (!p.suficiente) return <p className="aviso-proyeccion">{p.explicacion}</p>
  const decimales = unidad === 'kW' ? 1 : 0
  const f = (v: number) => v.toLocaleString('es-EC', { maximumFractionDigits: decimales })
  return (
    <div className="proyeccion">
      <div className="indicadores">
        <div className="indicador">
          <span className="indicador-titulo">Promedio proyectado</span>
          <span className="indicador-valor">
            {f(p.promedio ?? 0)} {unidad}/mes
          </span>
          <span className="indicador-detalle">máximo {f(p.maximo ?? 0)}</span>
        </div>
        <div className="indicador">
          <span className="indicador-titulo">Error de backtest</span>
          <span className="indicador-valor">{pctTexto(p.error_backtest_pct)} %</span>
          <span className="indicador-detalle">
            fuera de muestra, {p.predicciones_backtest} predicciones · promedio simple: {pctTexto(p.error_promedio_pct)} %
          </span>
        </div>
        <div className="indicador">
          <span className="indicador-titulo">Método</span>
          <span className="indicador-valor pequeno-valor">{METODO[p.metodo]}</span>
          <span className="indicador-detalle">{p.meses_historial} meses de historial</span>
        </div>
      </div>
      <Grafico historial={historial} proyeccion={p} unidad={unidad} />
      <p className="suave">{p.explicacion}</p>
      {p.avisos.map((a) => (
        <p key={a} className="suave pequeno-texto">
          ⚠ {a}
        </p>
      ))}
      <details>
        <summary>Los 12 meses proyectados</summary>
        <div className="tabla-envoltura">
          <table className="tabla compacta">
            <thead>
              <tr>
                <th>Mes</th>
                <th className="num">{unidad} proyectados</th>
                <th className="num">Intervalo (80 %)</th>
              </tr>
            </thead>
            <tbody>
              {p.meses.map((m) => (
                <tr key={m.periodo} className="sin-clic">
                  <td>{nombreMes(m.periodo)}</td>
                  <td className="num">{f(m.valor)}</td>
                  <td className="num">
                    {f(m.inferior)} – {f(m.superior)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  )
}
